import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase, isStaffSession } from "@/lib/supabase";
import { Login } from "@/routes/login";
import { Layout } from "@/components/Layout";
import { Dashboard } from "@/routes/dashboard";
import { OrdersIndex } from "@/routes/orders/index";
import { OrderDetail } from "@/routes/orders/detail";
import { OrderNew } from "@/routes/orders/new";
import { InviteIndex } from "@/routes/invite/index";
import { TicketsIndex } from "@/routes/tickets/index";
import { AmbassadorsIndex } from "@/routes/ambassadors/index";
import { Scan } from "@/routes/scan";
import { EventDay } from "@/routes/event-day";
import { TemplatesIndex } from "@/routes/templates/index";
import { TemplateEdit } from "@/routes/templates/edit";
import { MailingsIndex } from "@/routes/mailings/index";
import { EmailsIndex } from "@/routes/emails/index";
import { EmailDetail } from "@/routes/emails/detail";
import { InboxIndex } from "@/routes/inbox/index";
import { SponsorsIndex } from "@/routes/sponsors/index";
import { CmsEvent } from "@/routes/cms/event";
import { IosPwaGuide } from "@/components/IosPwaGuide";

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (session === undefined) {
    return (
      <div className="login-shell">
        <div className="spinner" />
      </div>
    );
  }

  if (!session) {
    return (
      <>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <IosPwaGuide isAuthenticated={false} />
      </>
    );
  }

  // Medewerker-modus (gedeeld code-account): afgeslankte weergave, alleen scanner + lijsten.
  const staff = isStaffSession(session);

  if (staff) {
    return (
      <>
        <Routes>
          <Route path="/login" element={<Navigate to="/tickets" replace />} />
          <Route path="/scan" element={<Scan />} />
          <Route element={<Layout />}>
            <Route path="/tickets" element={<TicketsIndex />} />
            <Route path="/ambassadeurs" element={<AmbassadorsIndex />} />
            <Route path="*" element={<Navigate to="/tickets" replace />} />
          </Route>
        </Routes>
        <IosPwaGuide isAuthenticated />
      </>
    );
  }

  // Scan route uses its own minimal layout (full-screen camera)
  return (
    <>
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/scan" element={<Scan />} />
        <Route path="/event-day" element={<EventDay />} />
        <Route element={<Layout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/orders" element={<OrdersIndex />} />
          <Route path="/orders/new" element={<OrderNew />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/tickets" element={<TicketsIndex />} />
          <Route path="/ambassadeurs" element={<AmbassadorsIndex />} />
          <Route path="/sponsors" element={<SponsorsIndex />} />
          <Route path="/invite" element={<InviteIndex />} />
          <Route path="/mailings" element={<MailingsIndex />} />
          <Route path="/emails" element={<EmailsIndex />} />
          <Route path="/emails/:id" element={<EmailDetail />} />
          <Route path="/inbox" element={<InboxIndex />} />
          <Route path="/templates" element={<TemplatesIndex />} />
          <Route path="/templates/:slug" element={<TemplateEdit />} />
          <Route path="/cms/event" element={<CmsEvent />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <IosPwaGuide isAuthenticated />
    </>
  );
}
